class MetafieldFilters extends HTMLElement {
  constructor() {
    super();
    this.selectors = {
      section: '.facest-filters-section',
      productGridContainer: '#CollectionProductGrid',
      filterCheckbox: 'input[type="checkbox"][data-filter-type]',
      clearAllBtn: '[data-clear-metafield-filters]',
      accordionToggle: '.s-filter-accordion-toggle',
      accordionContent: '.s-filter-accordion-content'
    };
    this.sectionId = '';
    this.loading = null;
    this.debouncedOnSubmit = this.debounce(this.onSubmitHandler.bind(this), 500);
    this.allProducts = [];
  }

  connectedCallback() {
    if (this.hasAttribute('data-initialized')) {
      return;
    }
    this.setAttribute('data-initialized', 'true');
    
    this.domNodes = this.queryDomNodes();
    this.setData();
    this.setLoadingTarget();
    this.initEventListeners();
    
    // Restore filter state from URL parameters on page load
    this.restoreFilterStateFromURL();
    
    // First, get products from current DOM for immediate display
    this.allProducts = this.getProductsFromDOM();
    this.updateFilterCount();
    this.updateFilterOptionCounts();
    
    // Check and update pagination visibility on initial load
    this.updatePaginationVisibility();
    
    // Then, fetch all products in the background and update counts
    this.cacheProductData()
    .then(() => {
      this.updateFilterCount();
      this.updateFilterOptionCounts();
      this.updatePaginationVisibility();
    })
    .catch(err => {
      console.error('Error caching product data:', err);
      // Use DOM products if fetch fails
      this.allProducts = this.getProductsFromDOM();
      this.updateFilterOptionCounts();
      this.updatePaginationVisibility();
    });
  }

  restoreFilterStateFromURL() {
    const params = new URLSearchParams(window.location.search);
    const styleFilter = params.get('filter.p.m.custom.style');
    const categoriesFilter = params.get('filter.p.m.custom.categories');
    
    // Restore style filter state
    if (styleFilter) {
      const styleValues = styleFilter.split(',').map(v => v.trim());
      this.querySelectorAll('input[data-filter-type="style"]').forEach(input => {
        const label = input.closest('label');
        if (styleValues.includes(input.dataset.filterValue)) {
          input.checked = true;
          input.dataset.waschecked = "true";
          if (label) label.classList.add('active');
        } else {
          input.checked = false;
          input.dataset.waschecked = "false";
          if (label) label.classList.remove('active');
        }
      });
    } else {
      // Clear all style filters
      this.querySelectorAll('input[data-filter-type="style"]').forEach(input => {
        const label = input.closest('label');
        input.checked = false;
        input.dataset.waschecked = "false";
        if (label) label.classList.remove('active');
      });
    }
    
    // Restore categories filter state
    if (categoriesFilter) {
      const categoriesValues = categoriesFilter.split(',').map(v => v.trim());
      this.querySelectorAll('input[data-filter-type="categories"]').forEach(input => {
        const label = input.closest('label');
        if (categoriesValues.includes(input.dataset.filterValue)) {
          input.checked = true;
          input.dataset.waschecked = "true";
          if (label) label.classList.add('active');
        } else {
          input.checked = false;
          input.dataset.waschecked = "false";
          if (label) label.classList.remove('active');
        }
      });
    } else {
      // Clear all categories filters
      this.querySelectorAll('input[data-filter-type="categories"]').forEach(input => {
        const label = input.closest('label');
        input.checked = false;
        input.dataset.waschecked = "false";
        if (label) label.classList.remove('active');
      });
    }
  }

  queryDomNodes() {
    const nodes = {};
    for (const [key, selector] of Object.entries(this.selectors)) {
      if (key.endsWith('Container') || key === 'section') {
        nodes[key] = document.querySelector(selector);
      } else {
        nodes[key] = this.querySelectorAll(selector);
      }
    }
    return nodes;
  }

  setData() {
    const section = this.domNodes.section;
    if (section) {
      this.sectionId = section.dataset.sectionId;
    }
    // Store collection handle from data attribute
    if (this.dataset.collectionHandle) {
      // Already set via data attribute
    }
  }

  setLoadingTarget() {
    const isMobile = window.innerWidth < 768;
    const targetContainer = this.domNodes.productGridContainer;
    
    if (typeof SifyUtils !== 'undefined' && SifyUtils.AnimateLoading) {
      this.loading = new SifyUtils.AnimateLoading(
        document.body,
        isMobile ? {} : { overlay: targetContainer }
      );
    }
  }

  initEventListeners() {
    if (this.listenersInitialized) {
      return;
    }
    this.debouncedOnSubmit = this.debounce(this.onSubmitHandler.bind(this), 500);
    this.addEventListener('click', this.handleClick.bind(this));
    this.addEventListener('change', (event) => {
      if (event.target.matches('input[data-filter-type]')) {
        this.debouncedOnSubmit(event);
        // 选择完后关闭下拉菜单
        this.closeDropdownMenus();
      }
    });
    
    // Intercept pagination clicks to preserve filter parameters
    document.addEventListener('click', (event) => {
      const paginationLink = event.target.closest('.custom-pagination__item[href]');
      if (paginationLink && paginationLink.href) {
        event.preventDefault();
        event.stopPropagation();
        
        const url = new URL(paginationLink.href);
        const currentParams = new URLSearchParams(window.location.search);
        
        // Preserve filter parameters
        const styleFilter = currentParams.get('filter.p.m.custom.style');
        const categoriesFilter = currentParams.get('filter.p.m.custom.categories');
        
        if (styleFilter) {
          url.searchParams.set('filter.p.m.custom.style', styleFilter);
        }
        if (categoriesFilter) {
          url.searchParams.set('filter.p.m.custom.categories', categoriesFilter);
        }
        
        // Navigate to the new URL with filters preserved
        window.location.href = url.toString();
      }
    }, true);

    // 允许radio再次点击取消选中
    this.addEventListener('click', (event) => {
      const target = event.target;
      if (target.matches('input[type="radio"][data-filter-type]')) {
        // 如果点击的是已选中的radio，准备取消选中
        if (target.checked && target.dataset.waschecked === "true") {
          event.preventDefault();
          event.stopPropagation();
          target.checked = false;
          target.dataset.waschecked = "false";
          // 触发change事件来更新筛选
          const evt = new Event('change', { bubbles: true });
          target.dispatchEvent(evt);
          // 选择完后关闭下拉菜单
          this.closeDropdownMenus();
        } else if (target.checked) {
          // 第一次选中，标记为可取消
          const name = target.name;
          document.querySelectorAll(`input[type="radio"][name="${name}"]`).forEach(r => {
            r.dataset.waschecked = "false";
          });
          target.dataset.waschecked = "true";
        }
      }
    });
    window.addEventListener('popstate', this.onHistoryChange.bind(this));
    this.listenersInitialized = true;
  }

  handleClick(event) {
    const target = event.target;
    if (target.matches(this.selectors.accordionToggle) || target.closest(this.selectors.accordionToggle)) {
      event.preventDefault();
      this.toggleAccordion(target.closest(this.selectors.accordionToggle) || target);
      return;
    }
    if (target.matches(this.selectors.clearAllBtn) || target.closest(this.selectors.clearAllBtn)) {
      event.preventDefault();
      this.clearAllFilters();
      return;
    }
  }
  
  onSubmitHandler(event) {
    if(event) {
        event.preventDefault();
    }
    const searchParams = this.getSearchParams();
    const url = this.updateURLHash(searchParams);
    const params = new URLSearchParams(searchParams);

    const nowstyle = params.get("filter.p.m.custom.style");
    const nowcategories = params.get("filter.p.m.custom.categories");

    fetch(url)
      .then(response => response.text())
      .then((data) => {
        this.renderProductGrid(data, async () => {
          // Only restore checked state if filters exist in URL
          if (nowcategories) {
        const categoriesInput = document.querySelector(`input[name="categories"][data-filter-value="${nowcategories}"]`);
            if (categoriesInput) {
              categoriesInput.checked = true;
              categoriesInput.dataset.waschecked = "true";
              // Clear waschecked for other categories in the same group
              document.querySelectorAll(`input[name="categories"]`).forEach(r => {
                if (r !== categoriesInput) {
                  r.dataset.waschecked = "false";
                }
              });
            }
          } else {
            // No categories filter, clear all categories radio buttons
            document.querySelectorAll(`input[name="categories"]`).forEach(r => {
              r.checked = false;
              r.dataset.waschecked = "false";
            });
          }

          if (nowstyle) {
        const styleInput = document.querySelector(`input[name="style"][data-filter-value="${nowstyle}"]`);
            if (styleInput) {
              styleInput.checked = true;
              styleInput.dataset.waschecked = "true";
              // Clear waschecked for other styles in the same group
              document.querySelectorAll(`input[name="style"]`).forEach(r => {
                if (r !== styleInput) {
                  r.dataset.waschecked = "false";
                }
              });
            }
          } else {
            // No style filter, clear all style radio buttons
            document.querySelectorAll(`input[name="style"]`).forEach(r => {
              r.checked = false;
              r.dataset.waschecked = "false";
            });
          }
          
          // Clear cached products and re-fetch with new filters
          this.allProducts = [];
          
          // Re-fetch all products with current filters to get accurate counts
          await this.cacheProductData();
          
          // Update filter counts after restoring checked state
    this.updateFilterOptionCounts();
          
          // Update pagination visibility after filtering
          this.updatePaginationVisibility();
        });
      });
  }

  async cacheProductData() {
    // Get collection handle from data attribute or URL
    const collectionHandle = this.dataset.collectionHandle || 
      (() => {
        const currentPath = window.location.pathname;
        const collectionMatch = currentPath.match(/\/collections\/([^\/]+)/);
        return collectionMatch ? collectionMatch[1] : null;
      })();
    
    if (!collectionHandle) {
      console.warn('Could not determine collection handle');
      // Use current page products as fallback
      this.allProducts = this.getProductsFromDOM();
      return;
    }
    
    const collectionUrl = `/collections/${collectionHandle}`;
    
    // IMPORTANT: Always fetch ALL products WITHOUT any filters for accurate count calculation
    // The counts should be calculated from all products in the collection
    // We explicitly remove any filter parameters to get unfiltered products
    const filterParams = new URLSearchParams();
    // Do NOT add any filter parameters - we want ALL products
    
    // Fetch all products from the collection by paginating through all pages
    let allProductsData = [];
    let page = 1;
    let totalPages = 1;
    let hasMore = true;
    const maxPages = 50; // Safety limit
    
    // First, get total pages from current page if available
    const currentProductContainer = document.querySelector('[data-product-container]');
    if (currentProductContainer) {
      totalPages = parseInt(currentProductContainer.dataset.totalPages) || 1;
    }
    
    while (hasMore && page <= maxPages) {
      try {
        // Build URL WITHOUT any filter parameters - we want ALL products
        const pageParams = new URLSearchParams(filterParams);
        pageParams.set('page', page);
        pageParams.set('limit', '250'); // Shopify's max per page
        // Explicitly remove any filter parameters if they exist
        pageParams.delete('filter.p.m.custom.style');
        pageParams.delete('filter.p.m.custom.categories');
        
        const url = `${collectionUrl}?${pageParams.toString()}`;
        const response = await fetch(url);
        
        if (!response.ok) {
          hasMore = false;
          break;
        }
        
        const html = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        
        // Extract product data from data attributes on product items
        const productItems = doc.querySelectorAll('.s-product-item[data-meta-style], .s-product-item[data-meta-categories]');
        
        if (productItems.length > 0) {
          // Add products from this page (with deduplication by product ID)
          const productIdsSeen = new Set();
          Array.from(productItems).forEach(item => {
            const productId = item.dataset.productId || item.closest('[data-product-id]')?.dataset.productId;
            const styleAttr = item.dataset.metaStyle || '';
            const categoriesAttr = item.dataset.metaCategories || '';
            
            // Use product ID for deduplication, or fallback to style+categories combination
            const uniqueKey = productId || `${styleAttr}|${categoriesAttr}`;
            
            if (!productIdsSeen.has(uniqueKey)) {
              productIdsSeen.add(uniqueKey);
              allProductsData.push({
                id: productId,
                style: styleAttr ? styleAttr.split(',').map(s => s.trim().replace(/[\[\]"]/g, '')) : [],
                categories: categoriesAttr ? categoriesAttr.split(',').map(c => c.trim().replace(/[\[\]"]/g, '')) : []
              });
            }
          });
          
          // Update total pages from the fetched page (in case it's different)
          const productContainer = doc.querySelector('[data-product-container]');
          if (productContainer) {
            const fetchedTotalPages = parseInt(productContainer.dataset.totalPages) || 1;
            if (fetchedTotalPages > totalPages) {
              totalPages = fetchedTotalPages;
            }
          }
          
          // Check if we've reached the last page
          if (page >= totalPages) {
            hasMore = false;
          } else {
            page++;
          }
        } else {
          // No products on this page, check if we should continue
          // If we're past the known total pages, stop
          if (page >= totalPages) {
            hasMore = false;
          } else {
            // Try next page in case totalPages wasn't accurate
            page++;
          }
        }
      } catch (error) {
        console.warn(`Error fetching page ${page}:`, error);
        hasMore = false;
      }
    }
    
    console.log(`Fetched ${allProductsData.length} products from ${page - 1} page(s), total pages: ${totalPages}`);
    
    // If we got data from fetching, use it
    if (allProductsData.length > 0) {
      this.allProducts = allProductsData;
      return;
    }
    
    // Fallback: Get product data from current page DOM elements
    this.allProducts = this.getProductsFromDOM();
  }
  
  getProductsFromDOM() {
    const products = document.querySelectorAll('.s-product-item');
    const productIdsSeen = new Set();
    const uniqueProducts = [];
    
    Array.from(products).forEach(product => {
      const productId = product.dataset.productId || product.closest('[data-product-id]')?.dataset.productId;
      const styleAttr = product.dataset.metaStyle || '';
      const categoriesAttr = product.dataset.metaCategories || '';
      
      // Use product ID for deduplication, or fallback to style+categories combination
      const uniqueKey = productId || `${styleAttr}|${categoriesAttr}`;
      
      if (!productIdsSeen.has(uniqueKey)) {
        productIdsSeen.add(uniqueKey);
        uniqueProducts.push({
          id: productId,
          style: styleAttr ? styleAttr.split(',').map(s => s.trim().replace(/[\[\]"]/g, '')) : [],
          categories: categoriesAttr ? categoriesAttr.split(',').map(c => c.trim().replace(/[\[\]"]/g, '')) : []
        });
      }
    });
    
    return uniqueProducts;
  }

  applyFilters() {
    const activeFilters = this.getActiveFilters();
    let visibleProducts = 0;
    
    this.allProducts.forEach(productData => {
      const { element, style: styleMeta, categories: categoriesMeta } = productData;
      
      let styleMatch = activeFilters.style.length === 0;
      if (!styleMatch) {
        // Use exact match instead of includes to avoid partial matches
        if (Array.isArray(styleMeta)) {
          styleMatch = activeFilters.style.some(filter => {
            const filterStr = String(filter).trim().toLowerCase();
            return styleMeta.some(meta => String(meta).trim().toLowerCase() === filterStr);
          });
        } else {
          const styleArray = String(styleMeta).split(',').map(s => s.trim().toLowerCase());
          styleMatch = activeFilters.style.some(filter => {
            const filterStr = String(filter).trim().toLowerCase();
            return styleArray.includes(filterStr);
          });
        }
      }
      
      let categoriesMatch = activeFilters.categories.length === 0;
      if (!categoriesMatch) {
        // Use exact match instead of includes to avoid partial matches
        if (Array.isArray(categoriesMeta)) {
          categoriesMatch = activeFilters.categories.some(filter => {
            const filterStr = String(filter).trim().toLowerCase();
            return categoriesMeta.some(meta => String(meta).trim().toLowerCase() === filterStr);
          });
        } else {
          const categoriesArray = String(categoriesMeta).split(',').map(c => c.trim().toLowerCase());
          categoriesMatch = activeFilters.categories.some(filter => {
            const filterStr = String(filter).trim().toLowerCase();
            return categoriesArray.includes(filterStr);
          });
        }
      }
      
      if (styleMatch && categoriesMatch) {
        element.style.display = '';
        visibleProducts++;
      } else {
        element.style.display = 'none';
      }
    });
    
    this.updateFilterCount(visibleProducts);
  }

  getActiveFilters() {
    const styleFilters = [];
    const categoriesFilters = [];
    // Support both radio and checkbox inputs
    this.querySelectorAll('input[data-filter-type="style"]:checked, input[type="radio"][data-filter-type="style"]:checked').forEach(input => {
      if (input.dataset.filterValue) {
        styleFilters.push(input.dataset.filterValue);
      }
    });
    this.querySelectorAll('input[data-filter-type="categories"]:checked, input[type="radio"][data-filter-type="categories"]:checked').forEach(input => {
      if (input.dataset.filterValue) {
        categoriesFilters.push(input.dataset.filterValue);
      }
    });
    return {
      style: styleFilters,
      categories: categoriesFilters
    };
  }

  getSearchParams() {
    const params = new URLSearchParams();
    const styleFilters = [];
    const categoriesFilters = [];
    // Support both radio and checkbox inputs
    this.querySelectorAll('input[data-filter-type="style"]:checked, input[type="radio"][data-filter-type="style"]:checked').forEach(input => {
      if (input.dataset.filterValue) {
        styleFilters.push(input.dataset.filterValue);
      }
    });
    this.querySelectorAll('input[data-filter-type="categories"]:checked, input[type="radio"][data-filter-type="categories"]:checked').forEach(input => {
      if (input.dataset.filterValue) {
        categoriesFilters.push(input.dataset.filterValue);
      }
    });
    if (styleFilters.length > 0) {
      params.set('filter.p.m.custom.style', styleFilters.join(','));
    }
    if (categoriesFilters.length > 0) {
      params.set('filter.p.m.custom.categories', categoriesFilters.join(','));
    }
    return params.toString();
  }

  toggleAccordion(toggle) {
    const content = toggle.nextElementSibling;
    const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
    toggle.setAttribute('aria-expanded', !isExpanded);
    content.style.display = isExpanded ? 'none' : 'block';
    const icon = toggle.querySelector('.s-filter-accordion-icon');
    if (icon) {
      icon.style.transform = isExpanded ? 'rotate(0deg)' : 'rotate(180deg)';
    }
  }

  closeDropdownMenus() {
    // 关闭所有水平下拉菜单
    document.querySelectorAll('.s-filter--widget-horizontal .s-filter--options-horizontal').forEach(function(content) {
      const toggle = content.previousElementSibling;
      if (toggle && toggle.getAttribute('aria-expanded') === 'true') {
        toggle.setAttribute('aria-expanded', 'false');
        content.style.display = 'none';
        const icon = toggle.querySelector('.s-filter-accordion-icon');
        if (icon) icon.style.transform = 'rotate(0deg)';
      }
    });
  }

  clearAllFilters() {
    this.querySelectorAll('input[type="checkbox"]').forEach(checkbox => {
      checkbox.checked = false;
    });
    this.onSubmitHandler();
  }

  renderPage(searchParams, updateHistory = true) {
    // This function is no longer needed for filtering.
  }

  renderProductGrid(html,callback) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    
    // Save accordion states before DOM update (by filter type)
    const accordionStates = {};
    this.querySelectorAll('.s-filter--widget-horizontal').forEach(widget => {
      const filterType = widget.dataset.filterType;
      const toggle = widget.querySelector('.s-filter-accordion-toggle');
      if (toggle && filterType) {
        const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
        accordionStates[filterType] = isExpanded;
      }
    });
    
    // Only replace the product container and pagination, not the entire section
    const newSection = doc.querySelector('.facest-filters-section');
    if (newSection) {
      const newProductContainer = newSection.querySelector('[data-product-container]');
      const currentProductContainer = document.querySelector('[data-product-container]');
      
      // Get pagination elements from both new and current DOM
      const newPagination = newSection.querySelector('.s-collection--pagination');
      const currentPagination = document.querySelector('.s-collection--pagination');
      
      if (newProductContainer && currentProductContainer) {
        // Replace the product container
        currentProductContainer.replaceWith(newProductContainer);
        
        // Replace pagination to ensure it reflects filtered product count
        // This fixes the issue where pagination shows wrong page count after filtering
        if (newPagination) {
          // New HTML has pagination (more than 1 page)
          if (currentPagination) {
            // Replace existing pagination
            currentPagination.replaceWith(newPagination);
          } else {
            // Insert new pagination after product container
            newProductContainer.parentNode.insertBefore(newPagination, newProductContainer.nextSibling);
          }
        } else {
          // New HTML has no pagination (1 page or less) - remove current pagination if exists
          if (currentPagination) {
            currentPagination.remove();
          }
        }
        
        // Update pagination visibility based on actual product count
        this.updatePaginationVisibility();
        
        // Don't update product data here - let the callback handle it after cacheProductData
        // This prevents duplicate updates and ensures we use the correct data source
        
        // Restore accordion states after a short delay to ensure DOM is ready
        setTimeout(() => {
          this.querySelectorAll('.s-filter--widget-horizontal').forEach(widget => {
            const filterType = widget.dataset.filterType;
            const wasExpanded = accordionStates[filterType];
            if (wasExpanded !== undefined && filterType) {
              const toggle = widget.querySelector('.s-filter-accordion-toggle');
              const content = widget.querySelector('.s-filter-accordion-content');
              if (toggle && content) {
                toggle.setAttribute('aria-expanded', wasExpanded);
                content.style.display = wasExpanded ? 'block' : 'none';
                const icon = toggle.querySelector('.s-filter-accordion-icon');
                if (icon) {
                  icon.style.transform = wasExpanded ? 'rotate(180deg)' : 'rotate(0deg)';
                }
              }
            }
          });
        }, 0);
        
        // Don't update filter counts here - let the callback handle it
        // This prevents duplicate updates
        
      if (typeof handleBackgroundImageLazyload === 'function') {
        handleBackgroundImageLazyload();
        }
      }
    }

    if (typeof callback === "function") {
      callback();
    }
  }

  updateFilterCount(count) {
    // Removed: s-filter-count-text display
  }

  updatePaginationVisibility() {
    const productContainer = document.querySelector('[data-product-container]');
    if (!productContainer) return;
    
    const visibleProducts = productContainer.querySelectorAll('.s-product-item:not([style*="display: none"])').length;
    const totalPages = parseInt(productContainer.dataset.totalPages) || 1;
    const paginationElement = document.querySelector('.s-collection--pagination');
    
    // Check if filters are active
    const urlParams = new URLSearchParams(window.location.search);
    const hasActiveFilters = urlParams.has('filter.p.m.custom.style') || urlParams.has('filter.p.m.custom.categories');
    
    // Calculate products per page (default 12, but could be different)
    const productsPerPage = 12; // Default, could be read from section settings
    
    // Hide pagination if:
    // 1. Only 1 page or less
    // 2. No products visible
    // 3. When filters are active and filtered products fit in one page
    if (paginationElement) {
      if (totalPages <= 1) {
        paginationElement.style.display = 'none';
      } else if (hasActiveFilters && visibleProducts <= productsPerPage) {
        // When filtered, if all products fit in one page, hide pagination
        paginationElement.style.display = 'none';
      } else if (visibleProducts <= 0) {
        paginationElement.style.display = 'none';
      } else {
        paginationElement.style.display = '';
      }
    }
  }

  hasActiveFilters() {
    const url = new URL(window.location);
    const params = new URLSearchParams(url.search);
    return params.has('style') || params.has('categories');
  }

  updateURLHash(searchParams) {
    const newUrl = `${window.location.pathname}${searchParams ? '?' + searchParams : ''}`;
    history.pushState({ searchParams }, '', newUrl);
    return newUrl;
  }

  scrollToTop() {
    const targetElement = document.getElementById('CollectionProductGrid');
    if (targetElement && typeof SifyTheme !== 'undefined' && SifyTheme.scrollIntoView) {
      SifyTheme.scrollIntoView(targetElement, { align: { topOffset: 80 } });
    }
  }

  async onHistoryChange(event) {
    const params = new URLSearchParams(window.location.search);
    const style = params.get('filter.p.m.custom.style')?.split(',') || [];
    const categories = params.get('filter.p.m.custom.categories')?.split(',') || [];
    
    // Update checkbox/radio states
    this.querySelectorAll('input[type="radio"], input[type="checkbox"]').forEach(checkbox => {
        const type = checkbox.dataset.filterType;
        const value = checkbox.dataset.filterValue;
        if (type === 'style') {
            checkbox.checked = style.includes(value);
        } else if (type === 'categories') {
            checkbox.checked = categories.includes(value);
        }
    });
    
    // Clear cached products and re-fetch with new filters
    this.allProducts = [];
    
    // Re-fetch all products with current filters
    await this.cacheProductData();
    
    this.applyFilters();
    this.updateFilterOptionCounts();
  }

  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  updateFilterOptionCounts() {
    // Prevent duplicate calls
    if (this._updatingCounts) {
      return;
    }
    this._updatingCounts = true;
    
    // Save accordion states before updating
    const accordionStates = {};
    this.querySelectorAll('.s-filter--widget-horizontal').forEach(widget => {
      const filterType = widget.dataset.filterType;
      const toggle = widget.querySelector('.s-filter-accordion-toggle');
      if (toggle && filterType) {
        const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
        accordionStates[filterType] = isExpanded;
      }
    });
    
    try {
      // Get active filters from URL parameters (more reliable than DOM state)
      const urlParams = new URLSearchParams(window.location.search);
      const activeFilters = {
        style: urlParams.get('filter.p.m.custom.style') ? urlParams.get('filter.p.m.custom.style').split(',').filter(f => f.trim()) : [],
        categories: urlParams.get('filter.p.m.custom.categories') ? urlParams.get('filter.p.m.custom.categories').split(',').filter(f => f.trim()) : []
      };
      
      // When calculating style counts, consider active categories filters
      // When calculating categories counts, consider active style filters
      // But also need to consider active filters of the same type (for radio buttons, exclude the current one being calculated)
      this.updateFilterTypeOptionCounts('style', activeFilters.categories, activeFilters.style);
      
      this.updateFilterTypeOptionCounts('categories', activeFilters.style, activeFilters.categories);
      
      // Restore accordion states after updating
      setTimeout(() => {
        this.querySelectorAll('.s-filter--widget-horizontal').forEach(widget => {
          const filterType = widget.dataset.filterType;
          const wasExpanded = accordionStates[filterType];
          if (wasExpanded !== undefined && filterType) {
            const toggle = widget.querySelector('.s-filter-accordion-toggle');
            const content = widget.querySelector('.s-filter-accordion-content');
            if (toggle && content) {
              toggle.setAttribute('aria-expanded', wasExpanded);
              content.style.display = wasExpanded ? 'block' : 'none';
              const icon = toggle.querySelector('.s-filter-accordion-icon');
              if (icon) {
                icon.style.transform = wasExpanded ? 'rotate(180deg)' : 'rotate(0deg)';
              }
            }
          }
        });
      }, 0);
    } finally {
      // Use setTimeout to allow async operations to complete
      setTimeout(() => {
        this._updatingCounts = false;
      }, 100);
    }
  }

  updateFilterTypeOptionCounts(filterType, otherActiveFilters, sameTypeActiveFilters) {
    // Support both radio and checkbox inputs
    const checkboxes = this.querySelectorAll(`input[data-filter-type="${filterType}"], input[type="radio"][data-filter-type="${filterType}"], input[type="checkbox"][data-filter-type="${filterType}"]`);
    
    if (checkboxes.length === 0) return;
    
    // Get products data for counting
    // IMPORTANT: Always use allProducts (all unfiltered products) as the base
    // Then apply filters to calculate accurate counts
    let productsToCount = [];
    
    // Check if filters are active from URL parameters (more reliable)
    const urlParams = new URLSearchParams(window.location.search);
    const hasActiveFilters = urlParams.has('filter.p.m.custom.style') || urlParams.has('filter.p.m.custom.categories');
    
    // Always use allProducts if available (contains all 57 products)
    // This ensures counts are calculated from the complete product set
    if (this.allProducts.length > 0) {
      // Use cached all products (already deduplicated, contains all 57 products)
      productsToCount = this.allProducts;
    } else {
      // Fallback: Get from current page DOM with deduplication
      // Note: This may not include all products if pagination is used
      const productItems = document.querySelectorAll('.s-product-item');
      const productIdsSeen = new Set();
      
      Array.from(productItems).forEach(item => {
        const productId = item.dataset.productId || item.closest('[data-product-id]')?.dataset.productId;
        const styleAttr = item.dataset.metaStyle || '';
        const categoriesAttr = item.dataset.metaCategories || '';
        
        // Use product ID for deduplication, or fallback to style+categories combination
        const uniqueKey = productId || `${styleAttr}|${categoriesAttr}`;
        
        if (!productIdsSeen.has(uniqueKey)) {
          productIdsSeen.add(uniqueKey);
          productsToCount.push({
            id: productId,
            style: styleAttr ? styleAttr.split(',').map(s => s.trim().replace(/[\[\]"]/g, '')) : [],
            categories: categoriesAttr ? categoriesAttr.split(',').map(c => c.trim().replace(/[\[\]"]/g, '')) : []
          });
        }
      });
    }
    
    // Apply filters to productsToCount to get the context for counting
    // When calculating counts, we need to consider:
    // 1. Active filters of the OTHER type (e.g., when counting style options, consider active categories filters)
    // 2. Active filters of the SAME type (e.g., when counting style options, consider other active style filters)
    // This ensures counts reflect how many products would match if this option is selected
    
    let filteredProducts = productsToCount;
    
    // First, apply filters of the OTHER type (cross-filter context)
    // For example, when counting style options, only count products that match active categories filters
    if (otherActiveFilters && otherActiveFilters.length > 0) {
      const otherFilterType = filterType === 'style' ? 'categories' : 'style';
      filteredProducts = filteredProducts.filter(productData => {
        const otherTypeMeta = productData[otherFilterType] || [];
        
        // Product must match at least one of the active filters of the other type
        if (Array.isArray(otherTypeMeta)) {
          return otherActiveFilters.some(filter => {
            const filterStr = String(filter).trim().toLowerCase();
            return otherTypeMeta.some(meta => {
              const metaStr = String(meta).trim().toLowerCase();
              return metaStr === filterStr;
            });
          });
        } else if (otherTypeMeta) {
          const otherMetaArray = String(otherTypeMeta).split(',').map(m => m.trim().toLowerCase());
          return otherActiveFilters.some(filter => {
            const filterStr = String(filter).trim().toLowerCase();
            return otherMetaArray.includes(filterStr);
          });
        }
        return false;
      });
    }
    
    // Then, apply filters of the SAME type (for context when multiple options of same type are active)
    // For example, when counting style options, only count products that match other active style filters
    if (sameTypeActiveFilters && sameTypeActiveFilters.length > 0) {
      filteredProducts = filteredProducts.filter(productData => {
        const sameTypeMeta = productData[filterType] || [];
        
        // Product must match at least one of the active filters of the same type
        if (Array.isArray(sameTypeMeta)) {
          return sameTypeActiveFilters.some(filter => {
            const filterStr = String(filter).trim().toLowerCase();
            return sameTypeMeta.some(meta => {
              const metaStr = String(meta).trim().toLowerCase();
              return metaStr === filterStr;
            });
          });
        } else if (sameTypeMeta) {
          const metaArray = String(sameTypeMeta).split(',').map(m => m.trim().toLowerCase());
          return sameTypeActiveFilters.some(filter => {
            const filterStr = String(filter).trim().toLowerCase();
            return metaArray.includes(filterStr);
          });
        }
        return false;
      });
    }
    
    checkboxes.forEach(checkbox => {
      const filterValue = checkbox.dataset.filterValue;
      if (!filterValue) return;
      
      const label = checkbox.closest('label') || checkbox.nextElementSibling;
      if (!label) return;
      
      let count = 0;
      const otherFilterType = filterType === 'style' ? 'categories' : 'style';
      
      // Count products that match this filter value and other active filters
      // Use filteredProducts which already respects active filters of the same type
      filteredProducts.forEach(productData => {
        const currentTypeMeta = productData[filterType] || [];
        const otherTypeMeta = productData[otherFilterType] || [];
        
        // Check if current product matches the filter value being calculated
        let currentMatch = false;
        if (Array.isArray(currentTypeMeta)) {
          // For arrays, check if the filter value is included (exact match)
          currentMatch = currentTypeMeta.some(meta => {
            const metaStr = String(meta).trim().toLowerCase();
            const filterStr = String(filterValue).trim().toLowerCase();
            return metaStr === filterStr;
          });
        } else if (currentTypeMeta) {
          // For strings, split by comma and check for exact match (case-insensitive)
          const metaArray = String(currentTypeMeta).split(',').map(m => m.trim().toLowerCase());
          const filterStr = String(filterValue).trim().toLowerCase();
          currentMatch = metaArray.includes(filterStr);
        }
        
        // Check if current product matches other active filters (different type)
        let otherMatch = otherActiveFilters.length === 0;
        if (!otherMatch) {
          if (Array.isArray(otherTypeMeta)) {
            // For arrays, check if any active filter is included (exact match)
            otherMatch = otherActiveFilters.some(filter => {
              const filterStr = String(filter).trim().toLowerCase();
              return otherTypeMeta.some(meta => {
                const metaStr = String(meta).trim().toLowerCase();
                return metaStr === filterStr;
              });
            });
          } else if (otherTypeMeta) {
            // For strings, split by comma and check for exact match
            const otherMetaArray = String(otherTypeMeta).split(',').map(m => m.trim().toLowerCase());
            otherMatch = otherActiveFilters.some(filter => {
              const filterStr = String(filter).trim().toLowerCase();
              return otherMetaArray.includes(filterStr);
            });
          }
        }
        
        // Only count if both conditions match
        if (currentMatch && otherMatch) {
          count++;
        }
      });
      
      // Update the data-filter-count attribute for use in updateFilterCount
      checkbox.dataset.filterCount = count;
      
      this.updateFilterOptionLabel(label, checkbox, count);
    });
  }

  updateFilterOptionLabel(label, checkbox, count) {
    let originalText = checkbox.dataset.originalText;
    
    // Check if this is a horizontal filter with s-filter-option-text span
    const optionTextSpan = label.querySelector('.s-filter-option-text');
    
    if (optionTextSpan) {
      // For horizontal filters, update the span content
      // Remove any existing count numbers from the text
      if (!originalText) {
        originalText = optionTextSpan.textContent.replace(/\s*\(\d+\)\s*/g, '').trim();
        checkbox.dataset.originalText = originalText;
      }
      
      // Don't add count - just use the original text without numbers
      optionTextSpan.textContent = originalText;
    } else {
      // For vertical filters (sidebar), update label text directly
      if (!originalText) {
        originalText = label.textContent.replace(/\s*\(\d+\)\s*/g, '').trim();
        checkbox.dataset.originalText = originalText;
      }
      
      // Don't add count - just use the original text without numbers
      const newText = originalText;
      
      const textNode = Array.from(label.childNodes).find(node => 
        node.nodeType === Node.TEXT_NODE && node.textContent.trim()
      );
      
      if (textNode) {
        textNode.textContent = newText;
      } else {
        const checkboxElement = label.querySelector('input');
        if (checkboxElement) {
          label.innerHTML = '';
          label.appendChild(checkboxElement);
          label.appendChild(document.createTextNode(newText));
        } else {
          label.textContent = newText;
        }
      }
    }
    
    if (count === 0) {
      label.style.display = "none";
    } else {
      label.style.display = 'block';
    }
  }
}

if (!customElements.get('metafield-filters')) {
  customElements.define('metafield-filters', MetafieldFilters);
}

document.addEventListener('DOMContentLoaded', function() {
  const components = document.querySelectorAll('metafield-filters');
  components.forEach((component) => {
    if (!component.hasAttribute('data-initialized')) {
      component.connectedCallback();
    }
  });
  // 点击下拉菜单以外区域关闭所有下拉菜单
  document.addEventListener('click', function(event) {
    document.querySelectorAll('.s-filter--widget-horizontal .s-filter--options-horizontal').forEach(function(content) {
      const toggle = content.previousElementSibling;
      if (toggle && toggle.getAttribute('aria-expanded') === 'true') {
        if (!content.parentElement.contains(event.target)) {
          toggle.setAttribute('aria-expanded', 'false');
          content.style.display = 'none';
          const icon = toggle.querySelector('.s-filter-accordion-icon');
          if (icon) icon.style.transform = 'rotate(0deg)';
        }
      }
    });
  });
}); 